// Seeded randomness for a bout.
//
// WHY THIS EXISTS. Every random decision in a bout used to come from `Math.random()`, which means a
// bout left no evidence behind: it could not be replayed, so it could not be verified, so a result
// reported by a browser was a claim the server had no way to check. `deploy_handoff/
// accounts-stage2-scope.md` is the long version. The short one is that a seed turns "he knocked me
// out on exchange 3 and it looked wrong" from an anecdote into a string that reproduces the bout.
//
// WHAT IS SEEDED AND WHAT DELIBERATELY IS NOT. This covers the resolution path only — js/engine.js
// and js/autoplay.js, the two modules that decide what actually happens. Three other uses of
// `Math.random()` in js/ are exempt on purpose, and tests/rng-determinism-test.js asserts the
// exemption list so a fourth cannot appear quietly:
//
//   - js/deck-store.js  — deck ids. A seeded id is a COLLIDING id: the whole point is that two
//                         devices offline generate different ones (deploy_handoff/
//                         accounts-stage1-scope.md §2).
//   - js/ui-online.js   — room codes. Guessable room codes are the bug.
//   - js/sfx.js         — audio noise. Cosmetic, per-sample, and not part of any result.
//
// The opponent's deck (js/ui.js) is also not drawn from the seed, and that is not an oversight: a
// replay takes the deck ids as INPUTS, because the logger already records them. Deriving them from
// the seed instead would mean a replay could not be pointed at a chosen matchup.
//
// TWO STREAMS, NOT ONE. `js/ui.js` gives the engine and the agent separate generators derived from
// the same bout seed. Sharing one stream would work only for as long as nobody ever asked the agent
// for a move it did not then play — a hint, a preview, a suggested line — because that one extra
// call would shift every subsequent draw and desynchronise the replay. Separate streams make that
// class of bug impossible rather than merely absent today.
window.RNG = (function () {
  // mulberry32 — small, fast, and good enough that a shuffle looks like a shuffle. The same
  // generator tests/fixture.js has used since D5; keeping it means fixture seeds behave the way
  // whoever wrote them expects.
  function mulberry32(a) {
    a = a >>> 0;
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  // FNV-1a, so a seed can be a readable string rather than a 32-bit integer nobody can type. This
  // is a mixing function and not a security primitive; a bout seed is public by design — it is
  // printed in the log and meant to be pasted into a bug report.
  function hashSeed(seed) {
    const s = String(seed);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }

  // A generator from any seed. Two calls with the same seed return two generators that produce the
  // same sequence, and are otherwise independent of each other.
  function make(seed) {
    return mulberry32(hashSeed(seed));
  }

  // A fresh seed for a new bout. Base36 in the same idiom as the deck ids in js/deck-store.js, so
  // it is short enough to read aloud and to paste into an issue.
  //
  // NOTE THE ABSENCE OF Math.random. Not fussiness: rng-determinism-test.js asserts that this file
  // contains none, because "the seeded module falls back to the unseeded thing" is exactly the
  // failure that would make every guarantee above quietly untrue. crypto is present in every
  // browser this game runs in and in Node 18+; the counter fallback exists so an exotic environment
  // degrades to unique-but-predictable rather than to throwing during boot.
  let counter = 0;
  function newSeed() {
    const c = (typeof crypto !== 'undefined' && crypto && crypto.getRandomValues) ? crypto : null;
    if (c) {
      const buf = new Uint32Array(2);
      c.getRandomValues(buf);
      return buf[0].toString(36) + buf[1].toString(36);
    }
    counter++;
    return Date.now().toString(36) + '-' + counter.toString(36);
  }

  return { make, newSeed, hashSeed };
})();
